import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const API = process.env.REACT_APP_API || '/api';

const STATUS_AR = {
  open: 'مفتوح',
  resolved_auto: 'حُسم آلياً',
  resolved_claude: 'حسمه Claude',
  needs_listening: 'يحتاج سماعاً',
  resolved_human: 'حسمه المدير',
};

const fmt = (s) => {
  if (s == null) return '';
  const t = Math.max(0, Math.floor(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

/**
 * مواضع المراجعة لتفريغ واحد: لكل بند مقطعه الصوتي (بهامش ثلاث ثوانٍ) والنصّان،
 * وقرار السامع بنقرة — يُطبَّق تصحيحاً موضعياً مسجّلاً يُعكس.
 */
export default function ReviewItems({ transcriptionId, status = 'open,needs_listening', onChange }) {
  const { api, token } = useAuth();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(null);
  const [audio, setAudio] = useState({});
  const urls = useRef([]);

  const load = useCallback(() => {
    api(`/transcriptions/${transcriptionId}/review-items${status ? `?status=${status}` : ''}`)
      .then(setData)
      .catch(err => toast.error(err.message));
  }, [api, transcriptionId, status, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => urls.current.forEach(u => URL.revokeObjectURL(u)), []);

  // <audio> لا يرسل ترويسة التفويض، فالمقطع يُجلب ثم يُشغَّل من الذاكرة
  const play = async (item) => {
    if (!item.clip_path) return;
    try {
      const res = await fetch(`${API}/${item.clip_path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const msg = await res.json().catch(() => null);
        throw new Error(msg?.error || 'تعذّر اقتطاع المقطع');
      }
      const url = URL.createObjectURL(await res.blob());
      urls.current.push(url);
      setAudio(a => ({ ...a, [item.id]: url }));
    } catch (err) { toast.error(err.message); }
  };

  const done = () => { load(); if (onChange) onChange(); };

  const choose = async (item, text) => {
    if (!text || !text.trim()) return;
    setBusy(item.id);
    try {
      await api(`/transcriptions/${transcriptionId}/review-items/${item.id}/choose`, {
        method: 'POST', body: JSON.stringify({ text }),
      });
      toast.success('حُسم البند');
      done();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const settle = async (item, next, resolution) => {
    setBusy(item.id);
    try {
      await api(`/transcriptions/${transcriptionId}/review-items/${item.id}`, {
        method: 'PUT', body: JSON.stringify({ status: next, resolution }),
      });
      done();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  if (!data) return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>جارِ التحميل...</div>;
  const items = Array.isArray(data.items) ? data.items : [];
  if (!items.length) {
    return <div style={{ fontSize: 12, color: 'var(--c-success)' }}>✓ لا مواضع تنتظر المراجعة</div>;
  }

  return (
    <div data-testid="review-items" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map(item => (
        <div key={item.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            <span className="badge badge-info">{item.kind_label}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{STATUS_AR[item.status] || item.status}</span>
            {item.start_sec != null && <span style={{ fontSize: 12 }}>⏱ {fmt(item.start_sec)}</span>}
            {item.clip_path && !audio[item.id] && (
              <button className="btn btn-outline btn-sm" onClick={() => play(item)}>▶ المقطع</button>
            )}
          </div>
          {audio[item.id] && <audio controls autoPlay src={audio[item.id]} style={{ width: '100%', marginBottom: 6 }} />}
          {item.context && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>…{item.context}…</div>}
          {item.resolution && <div style={{ fontSize: 12, marginBottom: 6 }}>💬 {item.resolution}</div>}
          {item.kind === 'coverage_gap' ? (
            <>
              <div style={{ fontSize: 13, marginBottom: 6 }}>الكلام الساقط: «{item.text_b}»</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                يُدرج بتصحيح موضعي في المتن بعد السماع، ويُحسم البند تلقائياً حين يصير كلامه في النصّ الخام.
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  className="form-input"
                  style={{ flex: 1, fontSize: 13 }}
                  placeholder="سبب الحسم دون إدراج (مثلاً: ليس من كلام الشيخ)"
                  value={custom[item.id] || ''}
                  onChange={e => setCustom(c => ({ ...c, [item.id]: e.target.value }))}
                />
                <button className="btn btn-sm btn-outline" disabled={busy === item.id || !custom[item.id]} onClick={() => settle(item, 'resolved_human', custom[item.id])}>حسم دون إدراج</button>
                {item.status === 'open' && (
                  <button className="btn btn-sm btn-outline" disabled={busy === item.id} onClick={() => settle(item, 'needs_listening', 'أُحيل للسماع')}>يحتاج سماعاً</button>
                )}
              </div>
            </>
          ) : (<>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {item.text_a && (
              <button className="btn btn-sm btn-outline" disabled={busy === item.id} onClick={() => choose(item, item.text_a)}>
                «{item.text_a}»
              </button>
            )}
            {item.text_b && item.kind !== 'hadith' && (
              <button className="btn btn-sm btn-outline" disabled={busy === item.id} onClick={() => choose(item, item.text_b)}>
                «{item.text_b}»
              </button>
            )}
            {item.kind === 'hadith' && item.text_b && (
              <span style={{ fontSize: 12 }}>عبارة الإسناد: {item.text_b}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input
              className="form-input"
              style={{ flex: 1, fontSize: 13 }}
              placeholder="أو اكتب الصحيح كما سمعته"
              value={custom[item.id] || ''}
              onChange={e => setCustom(c => ({ ...c, [item.id]: e.target.value }))}
            />
            <button className="btn btn-sm btn-primary" disabled={busy === item.id || !custom[item.id]} onClick={() => choose(item, custom[item.id])}>اعتماد</button>
            {item.status === 'open' && (
              <button className="btn btn-sm btn-outline" disabled={busy === item.id} onClick={() => settle(item, 'needs_listening', 'أُحيل للسماع')}>يحتاج سماعاً</button>
            )}
          </div>
          </>)}
        </div>
      ))}
    </div>
  );
}
